import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "pc_cookie_consent";
const CONSENT_VERSION = 1;

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c?.version !== CONSENT_VERSION) return null;
    return c;
  } catch { return null; }
}

function writeConsent(payload) {
  const doc = {
    version: CONSENT_VERSION,
    ts: new Date().toISOString(),
    necessary: true,
    ...payload,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(doc));
  window.dispatchEvent(new CustomEvent("pc:cookie-consent", { detail: doc }));
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!readConsent()) setVisible(true);
  }, []);

  if (!visible) return null;

  const acceptAll = () => { writeConsent({ analytics: true, marketing: true }); setVisible(false); };
  const rejectAll = () => { writeConsent({ analytics: false, marketing: false }); setVisible(false); };
  const savePrefs = () => { writeConsent(prefs); setVisible(false); };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4"
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieBanner.aria")}
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-4xl bg-black text-white rounded-lg border border-neutral-800 shadow-2xl overflow-hidden">
        {!customize ? (
          <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 text-sm leading-relaxed">
              <div className="font-display text-lg mb-1 text-[var(--pc-red)]">{t("cookieBanner.titulo")}</div>
              <p className="text-neutral-300">
                {t("cookieBanner.texto")}{" "}
                <Link to="/legal/cookies" className="text-[var(--pc-red)] underline">{t("cookieBanner.politicaCookies")}</Link>.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-shrink-0">
              <button onClick={() => setCustomize(true)} className="px-4 py-2 text-xs uppercase tracking-wider border border-neutral-600 hover:border-white rounded" data-testid="cookie-customize">
                {t("cookieBanner.personalizar")}
              </button>
              <button onClick={rejectAll} className="px-4 py-2 text-xs uppercase tracking-wider bg-neutral-800 hover:bg-neutral-700 rounded" data-testid="cookie-reject">
                {t("cookieBanner.rechazar")}
              </button>
              <button onClick={acceptAll} className="btn-red px-5 py-2 text-xs uppercase tracking-wider" data-testid="cookie-accept-all">
                {t("cookieBanner.aceptarTodo")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-5" data-testid="cookie-customize-panel">
            <div className="font-display text-lg mb-3 text-[var(--pc-red)]">{t("cookieBanner.configurarCookies")}</div>
            <div className="space-y-3 text-sm">
              <label className="flex items-start gap-3 p-3 bg-neutral-900 border border-neutral-800 rounded opacity-70 cursor-not-allowed">
                <input type="checkbox" checked disabled className="mt-1" />
                <div>
                  <div className="font-semibold">{t("cookieBanner.necesarias")}</div>
                  <div className="text-xs text-neutral-400">{t("cookieBanner.necesariasDesc")}</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 bg-neutral-900 border border-neutral-800 rounded cursor-pointer hover:border-[var(--pc-red)]">
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                  className="mt-1"
                  data-testid="cookie-pref-analytics"
                />
                <div>
                  <div className="font-semibold">{t("cookieBanner.analiticas")}</div>
                  <div className="text-xs text-neutral-400">{t("cookieBanner.analiticasDesc")}</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 bg-neutral-900 border border-neutral-800 rounded cursor-pointer hover:border-[var(--pc-red)]">
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={e => setPrefs(p => ({ ...p, marketing: e.target.checked }))}
                  className="mt-1"
                  data-testid="cookie-pref-marketing"
                />
                <div>
                  <div className="font-semibold">{t("cookieBanner.marketing")}</div>
                  <div className="text-xs text-neutral-400">{t("cookieBanner.marketingDesc")}</div>
                </div>
              </label>
            </div>
            <div className="flex flex-wrap gap-2 justify-end mt-4">
              <button onClick={() => setCustomize(false)} className="px-4 py-2 text-xs uppercase tracking-wider border border-neutral-600 hover:border-white rounded">{t("cookieBanner.volver")}</button>
              <button onClick={rejectAll} className="px-4 py-2 text-xs uppercase tracking-wider bg-neutral-800 hover:bg-neutral-700 rounded">{t("cookieBanner.rechazarTodo")}</button>
              <button onClick={savePrefs} className="btn-red px-5 py-2 text-xs uppercase tracking-wider" data-testid="cookie-save-prefs">
                {t("cookieBanner.guardarPreferencias")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
